import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import Form from 'react-bootstrap/Form';
import TaxSearchDropdown from '../../molecules/TaxSearchDropdown/view';
import ButtonLoading from '../../atoms/buttonLoading/view';
import { MEASURE_UNITS } from '../../../utils/measureUnits';
import { lookupProductByBarcode } from '../../../usecases/barcode-lookup-usecase';
import { checkProductDuplicate } from '../../../usecases/products-usecase';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { roleContext } from '../../../context/role/context';
import './style.css';

const ProductForm = ({
  initialData = {},
  mode = 'create',
  onSubmit,
  submitId = 'product-form-submit',
  submitLabel = 'Guardar',
}) => {
  const { profile } = useContext(roleContext);
  const isEdit = mode === 'edit';
  const isSet = (val) => val !== '' && val !== null && val !== undefined;

  const [formData, setFormData] = useState({
    sku: '',
    barcode: '',
    name: '',
    buy_value: '',
    value: '',
    stock: 0,
    sell_taxes_id: null,
    buy_taxes_id: null,
    unit_of_measure: 70,
    warranty: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(isEdit);
  const [isSearching, setIsSearching] = useState(false);
  const [autoFillEnabled, setAutoFillEnabled] = useState(true);
  const [duplicateFound, setDuplicateFound] = useState(null);

  const buffer = useRef('');
  const lastKeyTime = useRef(Date.now());
  const scannerTrapRef = useRef(null);
  const duplicateTimeout = useRef(null);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.sku?.toString().trim()) errors.sku = 'Requerido';
    if (!formData.name?.toString().trim()) errors.name = 'Requerido';
    if (!isSet(formData.buy_value)) errors.buy_value = 'Requerido';
    if (!isSet(formData.value)) errors.value = 'Requerido';
    if (isSet(formData.buy_value) && isSet(formData.value)) {
      if (parseFloat(formData.buy_value) > parseFloat(formData.value))
        errors.buy_value = 'Compra > Venta';
    }
    if (formData.buy_taxes_id === null || formData.buy_taxes_id === undefined)
      errors.buy_taxes_id = 'Requerido';
    if (formData.sell_taxes_id === null || formData.sell_taxes_id === undefined)
      errors.sell_taxes_id = 'Requerido';

    if (duplicateFound && !isEdit) {
      errors.sku = 'Ya existe';
      errors.barcode = 'Ya existe';
    }

    setFormErrors(errors);
    setIsFormValid(Object.keys(errors).length === 0);
  }, [formData, duplicateFound, isEdit]);

  const verifyDuplicate = useCallback(
    async (value) => {
      if (!value || isEdit) return;

      try {
        const duplicate = await checkProductDuplicate(
          profile.uid,
          profile.cid,
          value
        );
        if (duplicate) {
          setDuplicateFound(duplicate);
          dispatchCustomEvent('popup', {
            message: `⚠️ Código ya en uso por: ${duplicate.name} (SKU: ${duplicate.sku})`,
            type: 'warning',
          });
        } else {
          setDuplicateFound(null);
        }
      } catch (error) {
        console.error('Error validando duplicado:', error);
      }
    },
    [profile, isEdit]
  );

  const handleBarcodeLookup = useCallback(
    async (barcodeOverride = null) => {
      const codeToSearch = barcodeOverride || formData.barcode;
      if (!codeToSearch) return;

      setFormData((prev) => ({ ...prev, barcode: codeToSearch }));

      await verifyDuplicate(codeToSearch);

      setIsSearching(true);

      try {
        const info = await lookupProductByBarcode(codeToSearch);
        if (info) {
          setFormData((prev) => ({
            ...prev,
            barcode: codeToSearch,
            name: info.name || prev.name,
          }));
          dispatchCustomEvent('popup', {
            message: `¡Encontrado: ${info.name}!`,
            type: 'success',
          });
        } else {
          dispatchCustomEvent('popup', {
            message:
              'Producto no en base de datos. Completa los datos manualmente.',
            type: 'info',
          });
        }
      } catch (error) {
        console.error('Error en lookup:', error);
      } finally {
        setIsSearching(false);
      }
    },
    [formData.barcode, verifyDuplicate]
  );

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let parsed = value;
    if (type === 'number') parsed = value === '' ? '' : parseFloat(value);
    if (name === 'unit_of_measure') parsed = parseInt(value);

    setFormData((prev) => ({ ...prev, [name]: parsed }));

    if (name === 'sku' || name === 'barcode') {
      if (duplicateTimeout.current) clearTimeout(duplicateTimeout.current);
      duplicateTimeout.current = setTimeout(() => {
        verifyDuplicate(parsed);
      }, 700);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && autoFillEnabled && formData.barcode) {
      handleBarcodeLookup();
    }
  };

  useEffect(() => {
    if (Object.keys(initialData).length > 0) {
      setFormData({
        sku: initialData.sku || '',
        barcode: initialData.barcode || '',
        name: initialData.name || '',
        buy_value: initialData.buy_value ?? '',
        value: initialData.value ?? '',
        stock: initialData.stock ?? 0,
        sell_taxes_id: initialData.sell_taxes_id || null,
        buy_taxes_id: initialData.buy_taxes_id || null,
        unit_of_measure: initialData.unit_of_measure || 70,
        warranty: initialData.warranty || '',
      });
      if (isEdit) setIsFormValid(true);
    }
  }, [initialData, isEdit, mode]);

  useEffect(() => {
    if (!isEdit) validateForm();
  }, [formData, isEdit, validateForm]);

  const lookupRef = useRef(handleBarcodeLookup);
  useEffect(() => {
    lookupRef.current = handleBarcodeLookup;
  }, [handleBarcodeLookup]);

  useEffect(() => {
    if (!autoFillEnabled) return;

    const handleScannerInput = (e) => {
      const isInput =
        e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';
      const isTrap = e.target.id === 'scanner-trap';

      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime.current;
      lastKeyTime.current = currentTime;

      if (e.key === 'Enter') {
        if (buffer.current.length > 2) {
          e.preventDefault();
          e.stopPropagation();
          const code = buffer.current;
          buffer.current = '';
          lookupRef.current(code);
        }
      } else if (e.key.length === 1) {
        if (isTrap || !isInput || buffer.current.length > 0 || timeDiff < 40) {
          if (
            !isTrap &&
            isInput &&
            timeDiff > 100 &&
            buffer.current.length === 0
          ) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          buffer.current += e.key;

          clearTimeout(window.scannerTimeout);
          window.scannerTimeout = setTimeout(() => {
            buffer.current = '';
          }, 300);
        }
      }
    };

    document.addEventListener('keydown', handleScannerInput, true);
    return () => {
      document.removeEventListener('keydown', handleScannerInput, true);
      clearTimeout(window.scannerTimeout);
    };
  }, [autoFillEnabled]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (scannerTrapRef.current) scannerTrapRef.current.focus();
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = () => {
    if (!isEdit && !isFormValid) return;
    onSubmit(formData);
  };

  return (
    <div className="product-form-container">
      {/* TRAMPA DE ESCÁNER */}
      <input
        ref={scannerTrapRef}
        id="scanner-trap"
        type="text"
        style={{
          position: 'absolute',
          opacity: 0,
          pointerEvents: 'none',
          top: 0,
          left: 0,
        }}
        autoComplete="off"
      />

      <div className="product-form-header-tools">
        <label className="auto-fill-toggle">
          <span>Auto-completar datos al escanear</span>
          <input
            type="checkbox"
            checked={autoFillEnabled}
            onChange={(e) => setAutoFillEnabled(e.target.checked)}
          />
        </label>
      </div>

      <Form
        id={`${submitId}-form`}
        action="javascript:void(0);"
        style={{ width: '100%', outline: 'none', display: 'block' }}
        tabIndex={-1}
      >
        {/* Sección 1: Información Básica */}
        <div className="product-form-section-split">
          <div className="product-form-sidebar">
            <h3 className="product-form-section-title">Información Básica</h3>
            <p className="product-form-section-desc">Ingresa el nombre del producto o escanea su código de barras.</p>
          </div>

          <div className="product-form-fields-right">
            <div className="product-form-group">
              <label className="product-form-label">Nombre del Producto</label>
              <input
                className={`product-form-input ${formErrors.name ? 'is-invalid' : ''}`}
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nombre del producto"
              />
            </div>

            <div className="product-form-group">
              <label className="product-form-label">Código de Barras</label>
              <div className="product-form-input-group">
                <input
                  className={`product-form-input ${formErrors.barcode ? 'is-invalid' : ''}`}
                  type="text"
                  name="barcode"
                  id="product-barcode-input"
                  value={formData.barcode}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  readOnly={autoFillEnabled}
                  placeholder={
                    autoFillEnabled
                      ? 'Escanea el código con la pistola láser...'
                      : 'Escribe o escanea el código...'
                  }
                  autoComplete="off"
                  style={
                    autoFillEnabled
                      ? {
                        cursor: 'not-allowed',
                        backgroundColor: 'var(--color-gray-5)',
                      }
                      : {}
                  }
                />
                {isSearching && (
                  <span style={{ marginRight: '16px', fontSize: '0.8rem', color: 'var(--color-primary)' }}>
                    🏷️ Buscando...
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <hr className="product-form-separator" />

        {/* Sección 2: Identificación y Medidas */}
        <div className="product-form-section-split">
          <div className="product-form-sidebar">
            <h3 className="product-form-section-title">Identificación y Control</h3>
            <p className="product-form-section-desc">Establece el código único (SKU) y configuraciones de garantía.</p>
          </div>

          <div className="product-form-fields-right">
            <div className="product-form-fields-row">
              <div className="product-form-field-col">
                <div className="product-form-group">
                  <label className="product-form-label">SKU</label>
                  <input
                    className={`product-form-input ${formErrors.sku ? 'is-invalid' : ''}`}
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    disabled={isEdit}
                    placeholder="Ej: PROD-001"
                  />
                  {formErrors.sku && (
                    <div style={{ color: 'red', fontSize: '0.75rem', marginTop: '4px' }}>
                      {formErrors.sku}
                    </div>
                  )}
                </div>
              </div>

              <div className="product-form-field-col">
                <div className="product-form-group">
                  <label className="product-form-label">Unidad de Medida</label>
                  <select
                    className="product-form-select"
                    name="unit_of_measure"
                    value={formData.unit_of_measure}
                    onChange={handleChange}
                  >
                    {Object.entries(MEASURE_UNITS).map(([key, val]) => (
                      <option key={key} value={key}>
                        {val}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="product-form-fields-row">
              <div className="product-form-field-col">
                <div className="product-form-group">
                  <label className="product-form-label">Garantía (Meses/Años)</label>
                  <input
                    className="product-form-input"
                    type="text"
                    name="warranty"
                    value={formData.warranty}
                    onChange={handleChange}
                    placeholder="Ej: 3 meses, 1 año"
                  />
                </div>
              </div>

              {isEdit && (
                <div className="product-form-field-col">
                  <div className="product-form-group">
                    <label className="product-form-label">Existencias (Stock)</label>
                    <input
                      className="product-form-input"
                      type="number"
                      value={formData.stock}
                      disabled={true}
                      readOnly
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <hr className="product-form-separator" />

        {/* Sección 3: Valores y Tasas */}
        <div className="product-form-section-split">
          <div className="product-form-sidebar">
            <h3 className="product-form-section-title">Valores e Impuestos</h3>
            <p className="product-form-section-desc">Define los costos, márgenes e impuestos de compra y venta.</p>
          </div>

          <div className="product-form-fields-right">
            <div className="product-form-fields-row">
              <div className="product-form-field-col">
                <div className="product-form-group">
                  <label className="product-form-label">Valor Compra (Costo)</label>
                  <input
                    className={`product-form-input ${formErrors.buy_value ? 'is-invalid' : ''}`}
                    type="number"
                    name="buy_value"
                    value={formData.buy_value}
                    onChange={handleChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="product-form-field-col">
                <div className="product-form-group">
                  <label className="product-form-label">Valor Venta (Precio)</label>
                  <input
                    className={`product-form-input ${formErrors.value ? 'is-invalid' : ''}`}
                    type="number"
                    name="value"
                    value={formData.value}
                    onChange={handleChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="product-form-fields-row">
              <div className="product-form-field-col">
                <div className="product-form-group">
                  <label className="product-form-label">Impuesto Compra</label>
                  <TaxSearchDropdown
                    selected={
                      isSet(formData.buy_taxes_id)
                        ? { tax_id: formData.buy_taxes_id }
                        : undefined
                    }
                    onSelected={(e) =>
                      setFormData((prev) => ({ ...prev, buy_taxes_id: e.id }))
                    }
                    isInvalid={!!formErrors.buy_taxes_id}
                  />
                </div>
              </div>

              <div className="product-form-field-col">
                <div className="product-form-group">
                  <label className="product-form-label">Impuesto Venta</label>
                  <TaxSearchDropdown
                    selected={
                      isSet(formData.sell_taxes_id)
                        ? { tax_id: formData.sell_taxes_id }
                        : undefined
                    }
                    taxType={1}
                    onSelected={(e) =>
                      setFormData((prev) => ({ ...prev, sell_taxes_id: e.id }))
                    }
                    isInvalid={!!formErrors.sell_taxes_id}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>

      <div className="product-form-footer">
        <ButtonLoading
          id={submitId}
          title={submitLabel}
          onClick={handleSubmit}
          disabled={!isEdit && !isFormValid}
        />
      </div>
    </div>
  );
};

export default ProductForm;
